import { editRequestWebSocket } from "@/aawebsocket/websocket.gen";
import { appState } from "@/app/appState";
import { ChatTag, chatState } from "@/app/chatState";
import { ActionHandler, EntryItem, useRefState } from "@/components/UI/tools"
import { useCallback, useEffect, useMemo, useState } from "react";
import { Request } from "@/components/CRUD";
import { AdminUpdateTagData } from "@/api/types.gen";
import { adminUpdateTag, chatUpdateTag, updateTagSpeakerProfile } from "@/api/sdk.gen";
import { SpeakerProfileRecorder } from "@/components/SpeakerProfileRecorder";
import { appendProfile } from "@/utils/speakerProfileSerializer";
import { removeEmptyLikeFields, setHasChagedCallback, shortenLabel } from "../chatObjectEditorWindow";
import { Calendar } from "@/components/UI/svgs";
import { tag } from "@/app/typing/tags";
import { applyDiffs } from "@/components/diffs";
import { createPatch } from "diff";
import { has, runInAction, set } from "mobx";
import { observer } from "mobx-react-lite";
import { dispersedMatch } from "@/components/searchUtils";
import { client } from "@/api/client.gen";

import { useAttributesUnfurler } from "./useAttributesUnfurler";

export const TagEditor = observer(() => {

    const [refresh, setRefresh] = useState(0);
    const [loading, setLoading] = useState(false);

    const [id, setId] = useState<number | undefined>(undefined);
    const [revisionId, setRevisionId] = useState<number | undefined>(undefined);

    const [deleting, setDeleting] = useState(false);
    const [lock, setLock] = useRefState<boolean>(false);

    const [effectivePermissionType, setEffectivePermissionType] = useRefState<number | undefined>(undefined);
    const [isDraft, setIsDraft] = useRefState(false)
    const [edit, setEdit] = useState(false)

    const [revisionPage, setRevisionPage] = useState(0)
    const [maxRevPage, setMaxRevPage] = useState(0)

    const [organisationSearch, setOrganisationSearch] = useState<string>("")


    const validateTag = (obj: any) => {
        const keys = [
            'id',
            'name',
            'description',
            'created_at',
            'last_updated_at',
            'scope',
            'tpr_organisation_ids',
            'permission_type',
            'is_draft',
            'user_lock'
        ]

        if (!obj || typeof obj !== 'object' || !("tag" in obj)) {
            throw new Error("Invalid tag object");
        }

        for (const key of keys) {
            if (!(key in obj.tag)) {
                throw new Error(`Missing key ${key} in tag object`);
            }
        }

        return obj as ChatTag;

    }

    const objState = useMemo(() => {
        return runInAction(() => {
            const key = chatState.selectedObjectKey;
            setRefresh((prev) => prev + 1);



            if (key && key?.type == "tag") {
                let obj: ChatTag | null = null;
                let revision_page = 0
                obj = key ? (chatState.chatObjects.get(key) as ChatTag) : null
                console.log("REFRESHING REFS", key, obj)
                revision_page = obj?.revision_index !== undefined ? obj?.revision_index : -1
                setRevisionPage(revision_page)

                try {
                    obj = validateTag(obj)
                } catch (e) {
                    chatState.getObjectByKey({ type: "tag", id: key.id })
                    console.error("Error validating tag object", e)
                    return
                }

                const revisions = obj?.revisions?.slice().sort((a, b) => a.id - b.id).filter(x => x ? true : false) ?? []
                setMaxRevPage(revisions.length)

                if (!('description' in obj.tag)) {
                    console.error("Tag object is missing description field", obj)
                    return
                }

                if (revisions.length > 0) {
                    if (revision_page > revisions.length)
                        return

                    const maxIndex = revision_page + 1
                    const isLastRevision = maxIndex == revisions.length
                    const isRoot = revision_page == -1

                    setEdit(isLastRevision)
                    const curr = revisions[revision_page] ?? obj.tag
                    const revStrings = revisions.slice(0, maxIndex).map(x => x?.description_diff) ?? []


                    const root = obj.tag.description ?? ""

                    if (curr) {

                        setId(obj.id)
                        setIsDraft(obj.tag.is_draft)
                        setEffectivePermissionType(obj.tag.effective_permission_type)
                        setRevisionId(isRoot ? undefined : curr.id)
                        setLock(obj.tag.user_lock ? !(obj.tag.user_lock == appState.user?.id) : false)

                        const description = applyDiffs(root, [...revStrings])



                        const old_obj = {
                            name: curr.name,
                            description,
                            scope: curr.scope,
                            permission_type: curr.permission_type,
                            tpr_organisation_ids: curr.tpr_organisation_ids,
                            attributes: obj.tag.attributes,
                            has_calendar: obj.tag.has_calendar
                        }

                        setHasChagedCallback(old_obj)

                        const name = obj?.temp_obj?.name ?? obj.tag.name ?? ""

                        if (isLastRevision) {
                            return { ...old_obj, ...removeEmptyLikeFields(obj.temp_obj), name: name }
                        } else {
                            return { ...old_obj, name: name };
                        }
                    }

                }
                else {
                    if (obj) {

                        setIsDraft(obj.tag.is_draft)
                        setId(obj.id)
                        setEffectivePermissionType(obj.tag.effective_permission_type)
                        setRevisionId(undefined)
                        setEdit((obj.tag.is_draft || obj.unsaved_changes) ? true : (obj?.edit ?? false))
                        setLock(obj.tag.user_lock ? !(obj.tag.user_lock == appState.user?.id) : false)

                        const old_obj = {
                            name: obj.tag.name,
                            description: obj.tag.description,
                            scope: obj.tag.scope,
                            permission_type: obj.tag.permission_type,
                            tpr_organisation_ids: obj.tag.tpr_organisation_ids,
                            attributes: obj.tag.attributes,
                            has_calendar: obj.tag.has_calendar
                        }

                        setHasChagedCallback(old_obj);


                        return {
                            ...old_obj,
                            ...removeEmptyLikeFields(obj.temp_obj)
                        };
                    } else {
                        setRevisionId(undefined)
                        setIsDraft(false)

                        setLock(true)
                        setId(undefined)
                        setEffectivePermissionType(undefined)

                        return {
                            name: "",
                            description: "",
                            scope: undefined,
                            permission_type: undefined,
                            tpr_organisation_ids: [],
                            has_calendar: false,
                            attributes: undefined
                        };

                    }
                }
            }
        })


    },
        [
            chatState.selectedObjectKey,
            (chatState.chatObjects.get(chatState.selectedObjectKey) as ChatTag)?.tag,
            ((chatState.chatObjects.get(chatState.selectedObjectKey) as ChatTag)?.tag as tag)?.description,
            (chatState.chatObjects.get(chatState.selectedObjectKey) as ChatTag)?.revision_index,
            chatState.chatObjects.get(chatState.selectedObjectKey)?.edit,
            chatState.chatObjects.get(chatState.selectedObjectKey)?.revisions?.length,
            chatState.lastExternalUpdate
        ]);

    const currentObj: any = {
        ...objState, ...removeEmptyLikeFields((chatState.chatObjects.get(chatState.selectedObjectKey))?.temp_obj)
    }

    const publishRevision = useCallback(async (p_is_draft: boolean | null = null) => { // is_draft argument serves to have this as a publish funcitons
        setLoading(true);
        // if we have edits, and we are on the latest revision, send those as new description diff
        const isRoot = revisionId == undefined
        const isLastRevision = revisionPage == (maxRevPage - 1)
        const name = currentObj?.name ?? ""
        const tagObj = chatState.chatObjects.get(chatState.selectedObjectKey!) as ChatTag | undefined
        const revisions = tagObj?.revisions?.slice().sort((a, b) => a.id - b.id).filter(x => x ? true : false) ?? []
        const root = tagObj && 'description' in tagObj.tag ? tagObj.tag.description ?? "" : ""
        const revisionView = applyDiffs(root, revisions.slice(0, revisionPage + 1).map(x => x?.description_diff))
        const editedDescription = currentObj?.description ?? ""
        // On a revision the backend applies `description` as a diff on top of it.
        const description = !revisionId
            ? objState?.description
            : (isLastRevision && editedDescription !== revisionView ? createPatch("", revisionView, editedDescription) : undefined)

        if (!objState) {
            setLoading(false);
            return;
        }

        try {
            // Get values from refs directly

            if (id && objState.name && objState.scope && chatState.conversationId && isDraft.current != undefined) {
                const response: any = await chatUpdateTag({
                    body: {
                        id: id,
                        conversation_id: chatState.conversationId,
                        description: description,
                        scope: objState.scope,
                        permission_type: objState.permission_type,
                        tpr_organisation_ids: objState.tpr_organisation_ids,
                        attributes: objState.attributes,
                        name: name,
                        is_draft: p_is_draft == null ? isDraft.current : p_is_draft,
                        revision_id: revisionId ?? null
                    },
                });

                if (response.data) {
                    runInAction(() => {
                        if (isRoot || isLastRevision)
                            chatState.chatObjects.update(chatState.selectedObjectKey ? [{
                                key: chatState.selectedObjectKey,
                                value: {
                                    unsaved_changes: false,
                                    tag: {
                                        name: name,
                                        description: currentObj?.description,
                                        scope: currentObj?.scope,
                                        permission_type: currentObj?.permission_type,
                                        tpr_organisation_ids: currentObj?.tpr_organisation_ids,
                                        attributes: currentObj?.attributes,
                                    }
                                }
                            }] : [])
                        chatState.setObjectEditorTemp(null)
                        if (chatState.selectedObjectKey) {
                            chatState.getObjectByKey({ type: "tag", id: chatState.selectedObjectKey?.id })
                        }
                        chatState.chatObjects.update(chatState.selectedObjectKey ? [{
                            key: chatState.selectedObjectKey,
                            value: { revision_index: -1 }
                        }] : [])
                        if (id)
                            chatState.flushRevisions({ type: "tag", id: id }, revisionId) // TODO flush to revision id, remove chat_refresh to avoid dublicating revisions
                        appState.getTags({ pageNumber: 0, search: "" });
                    })

                    runInAction(() => {
                        appState.chat_refresh = appState.chat_refresh + 1 // TODO FIX THIS ABOMINAION
                        chatState.setSelectedObjectKey({ type: 'tag', id: id }, true)
                    })
                    setEdit(false)

                    return true;
                } else {
                    if (response.error) {
                        console.error("Error occurred:", response);
                        appState.setModal({ title: appState.t("common.error"), body: appState.t("objects.errorUpdateTag") + " - " + response.error.detail });
                        setRefresh((p) => p + 1);
                    }
                }
            } else {
                console.error("Missing requried field!", "id", id, "name", objState?.name, "description len", objState?.description?.length, "scope", objState?.scope, "permission_type", objState?.permission_type, "tpr_organisation_ids", objState?.tpr_organisation_ids, "isDraft", isDraft.current != undefined);
            }
        } finally {
            setLoading(false);

        }
    }, [id,
        currentObj?.name,
        currentObj?.scope,
        currentObj?.permission_type,
        currentObj?.tpr_organisation_ids,
        currentObj?.description,
        currentObj?.attributes,

        revisionId,
        revisionPage,
        maxRevPage
    ]); // all values sent should go here


    const updateTag = Request<AdminUpdateTagData, any>(
        adminUpdateTag,
        () => ({
            body: {
                id: chatState.selectedObjectKey?.id!,
                name: currentObj?.name ?? "",
                description: currentObj?.description ?? "",
                scope: currentObj?.scope ?? "me",
                permission_type: currentObj?.permission_type ?? 7,
                tpr_organisation_ids: currentObj?.tpr_organisation_ids ?? [],
                attributes: currentObj?.attributes,
            },
        }),
        setLoading,
        (data) => {
            runInAction(() => {
                // Tag needs to be hard set, to trigger refreshref, and create isChanged handler
                chatState.chatObjects.update(chatState.selectedObjectKey ? [{
                    key: chatState.selectedObjectKey,
                    value: {
                        unsaved_changes: false,
                        tag: {
                            name: objState?.name,
                            attributes: objState?.attributes,
                            description: objState?.description,
                            scope: objState?.scope,
                            permission_type: objState?.permission_type,
                            tpr_organisation_ids: objState?.tpr_organisation_ids
                        },
                    }
                }] : [])
                chatState.setObjectEditorTemp(null)

                if (chatState.selectedObjectKey) {
                    chatState.flushRevisions({ type: "tag", id: chatState.selectedObjectKey?.id })
                    chatState.getObjectByKey({ type: "tag", id: chatState.selectedObjectKey?.id })
                }
                appState.getTags({ pageNumber: 0, search: "" });
            })

        },
        (error) => {
            setRefresh(p => p + 1)
            appState.setModal({
                title: appState.t("common.error"),
                body: appState.t("objects.errorUpdateTag"),
                comment: { name: appState.t("common.detail"), val: error.detail?.toString() ?? "" },
            })
        },
        () => chatState.selectedObjectKey?.id != undefined && chatState.selectedObjectKey?.id > 0,
    )


    const updateAttribute = (key: string, val: any) => {
        chatState.setObjectEditorTemp({ attributes: { ...(currentObj?.attributes || {}), [key]: val } });
    };

    const attributesUnfurled = useAttributesUnfurler(currentObj?.attributes, updateAttribute);

    const dateOptions: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'numeric', day: 'numeric' };

    return (
        id && id == chatState.selectedObjectKey?.id ?
            <div>
                <EntryItem
                    isRevisionRoot={(revisionId === undefined) && (maxRevPage > 0)}
                    forcedEdit={(isDraft.current || maxRevPage > 0) ? true : false}
                    unsavedChanges={(chatState.chatObjects.get(chatState.selectedObjectKey!) as ChatTag)?.unsaved_changes ?? false}
                    lock={lock.current}
                    setEdit={async (val: boolean) => {
                        if (val)
                            await editRequestWebSocket(chatState.selectedObjectKey?.id ?? -1, (res) => {
                                chatState.chatObjects.update(chatState.selectedObjectKey ? [{ key: chatState.selectedObjectKey, value: { edit: res != null } }] : [])
                            }, "tag")
                        else
                            chatState.chatObjects.update(chatState.selectedObjectKey ? [{ key: chatState.selectedObjectKey, value: { edit: false } }] : [])
                    }}
                    edit={edit}
                    name={isDraft.current ? appState.t("chat.tool.draftTag") : appState.t("objects.list.tag")}
                    created_at={new Date((chatState.chatObjects.get(chatState.selectedObjectKey!) as ChatTag)?.tag.created_at ?? "").toLocaleDateString(undefined, dateOptions)}
                    loading={loading}
                    deleting={deleting}
                    readonly={((![4, 5, 6, 7].includes(appState.user?.active_organisation.permission_type ?? 0) || ![4, 5, 6, 7].includes(effectivePermissionType.current ?? 0)))}
                    chatEditorWindowClose={() => {
                        chatState.removeSelectedObjectKey()
                    }
                    }
                    updateEntry={((maxRevPage > 0 || chatState.conversationId) ? () => publishRevision(false) : updateTag)}
                    refreshCount={refresh}
                    idParams={{
                        singular: { get: chatState.selectedObjectKey?.id, set: () => { } },
                        plural: { get: [], set: () => { } },
                    }}
                    refColumns={[
                        {
                            type: (currentObj as any)?.attributes?.is_client ? "textarea" : "richtext",
                            label: appState.t("objects.description"),
                            value: currentObj?.description,
                            onChange: (value: string | undefined) => chatState.setObjectEditorTemp({ description: value }),
                            validator: (val: string | undefined) => (val ? true : false),
                        },
                        ...attributesUnfurled.refColumns,
                    ]}
                    nameColumn={{
                        type: "text",
                        value: currentObj?.name ?? "",
                        onChange: (x) => chatState.setObjectEditorTemp({ name: x }),
                        validator: (val: string) => /^[\p{L}0-9_\s-]+$/u.test(val),
                        label: appState.t("objects.variableName"),
                    }}
                    stateColumns={[{
                        label: appState.t("objects.scope"),
                        type: "switch",
                        onChange: (x) => {
                            const scope = x === appState.t("objects.scope.me") || x === "me" ? "me"
                                : x === appState.t("objects.scope.organisation") || x === "organisation" ? "organisation"
                                : undefined;
                            chatState.setObjectEditorTemp({ scope });
                        },
                        value: currentObj?.scope === "me" ? appState.t("objects.scope.me")
                            : currentObj?.scope === "organisation" ? appState.t("objects.scope.organisation")
                            : currentObj?.scope,
                        items: [{ name: appState.t("objects.scope.me") }, { name: appState.t("objects.scope.organisation") }],
                        validator: (val?: string) => val ? true : false,
                    },
                    {
                        label: appState.t("objects.access"),
                        type: "single",
                        onChange: (x) => chatState.setObjectEditorTemp({ permission_type: x }),
                        value: currentObj?.permission_type,
                        items: [{ id: 7, name: appState.t("objects.access.full") }, { id: 3, name: appState.t("objects.access.read") }, { id: 1, name: appState.t("objects.access.use") }],
                        validator: (val: number | undefined) => val ? true : false,
                    },
                    currentObj?.scope == "organisation" ?
                        {
                            label: appState.t("objects.shareTo"),

                            type: "multiple",
                            onChange: (x) => chatState.setObjectEditorTemp({ tpr_organisation_ids: x }),
                            search: { get: organisationSearch, set: setOrganisationSearch },
                            value: currentObj?.tpr_organisation_ids ?? [],
                            items: appState.organisations?.filter(x => {
                                if (x.id == appState.user?.active_organisation.id) return false;
                                return dispersedMatch(organisationSearch, x.name);
                            }).map((x) => {
                                return { id: x.id, name: x.name, defaultValue: currentObj?.tpr_organisation_ids?.includes(x.id) };
                            }),
                            validator: (val: number[]) => true,
                        }
                        :
                        undefined,
                    ...attributesUnfurled.stateColumns.filter(x => x.label != "is_client")
                    ]}
                />

                {/* Calendar & Google Sync Buttons */}
                {[4, 5, 6, 7].includes(effectivePermissionType.current ?? 0) &&
                    <div className="flex flex-row justify-end gap-0 px-[12px] pb-[6px]">
                        {/*Calendar indicator*/}
                        <ActionHandler
                            title={appState.t("objects.calendarActive")}
                            hidden={!currentObj?.has_calendar}
                            funq={(e) => { }}
                            disabled={true}
                            glow={currentObj?.has_calendar}
                        >
                            <Calendar />
                        </ActionHandler>

                        <ActionHandler
                            title={appState.t("objects.voiceProfile")}
                            keysTrigger=""
                            funq={async () => {

                                let deviceLabel = "current microphone";
                                try {
                                    const temporaryStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                                    deviceLabel = temporaryStream.getAudioTracks()[0]?.label || "current microphone";
                                    temporaryStream.getTracks().forEach(t => t.stop());
                                } catch (err) {
                                    console.warn("Could not get microphone for label", err);
                                }

                                appState.appendModal({
                                    title: appState.t("objects.voiceProfile"),
                                    body: (
                                        currentObj?.attributes?.speaker_profile && currentObj?.attributes?.speaker_profile.length > 0
                                            ? (
                                                <div className="flex flex-col">
                                                    <div className="flex flex-col w-full mb-1">
                                                        <p>Tag already has a voice profile.</p>
                                                        <p>You can remove it with the button below.</p>
                                                    </div>
                                                    <div className="flex flex-row w-full justify-start">
                                                        <ActionHandler
                                                            funq={() => {
                                                                updateTagSpeakerProfile({ body: { id: id!, speaker_profile: [] } })
                                                                    .then(() => {
                                                                        runInAction(() => {
                                                                            chatState.chatObjects.update([{
                                                                                key: chatState.selectedObjectKey!,
                                                                                value: { tag: { attributes: { ...(currentObj?.attributes || {}), speaker_profile: [] } } }
                                                                            }]);
                                                                            appState.getTags({ pageNumber: 0, search: "" });
                                                                            appState.popModal();
                                                                        });
                                                                    });
                                                            }}
                                                        >{appState.t("objects.clearSpeakerProfile")}</ActionHandler>
                                                    </div>
                                                </div>)
                                            : (
                                                <div className="p-2">

                                                    <SpeakerProfileRecorder
                                                        existingProfile={currentObj?.attributes?.speaker_profile}
                                                        label={currentObj?.name ?? "tag"}
                                                        deviceLabel={deviceLabel}
                                                        onProfileCaptured={(profile) => {
                                                            const updatedProfile = appendProfile(currentObj?.attributes?.speaker_profile, deviceLabel, profile);
                                                            updateTagSpeakerProfile({ body: { id: id!, speaker_profile: updatedProfile } })
                                                                .then(() => {
                                                                    runInAction(() => {
                                                                        chatState.chatObjects.update([{
                                                                            key: chatState.selectedObjectKey!,
                                                                            value: { tag: { attributes: { ...(currentObj?.attributes || {}), speaker_profile: updatedProfile } } }
                                                                        }]);
                                                                        appState.getTags({ pageNumber: 0, search: "" });
                                                                    });
                                                                });
                                                        }}
                                                    />
                                                </div>
                                            )

                                    )

                                });
                            }}
                            glow={currentObj?.attributes?.speaker_profile && currentObj?.attributes?.speaker_profile.length > 0}
                            disabled={loading}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                                <line x1="12" y1="19" x2="12" y2="23" />
                                <line x1="8" y1="23" x2="16" y2="23" />
                            </svg>
                        </ActionHandler>
                    </div>
                }

            </div >
            :
            null
    )

})